import type {
  FieldAssignmentFilter,
  FieldControlRepository,
  FieldInspectionAssignment,
  FieldInspectionProgress,
  FieldProgressFilter
} from "../../../domain/field-control/field-inspection-progress.js";

const PROGRESS_KEY = "inspect_field_progress";
const ASSIGNMENT_KEY = "inspect_field_assignments";

/**
 * Controle de Campo em memória — sessão local. NÃO é a fonte multiusuário:
 * o apontamento feito aqui não chega a outro usuário. A composição oficial do
 * app centralizado usa `SqliteFieldControlRepository`; esta implementação
 * serve ao modo local/offline e aos testes, seguindo o mesmo contrato.
 */
export class InMemoryFieldControlRepository implements FieldControlRepository {
  private progress = new Map<string, FieldInspectionProgress>();
  private assignments = new Map<string, FieldInspectionAssignment>();

  constructor() {
    this.progress = this.load(PROGRESS_KEY);
    this.assignments = this.load(ASSIGNMENT_KEY);
  }

  private load<T>(key: string): Map<string, T> {
    try {
      if (typeof localStorage !== "undefined") {
        const raw = localStorage.getItem(key);
        if (raw) return new Map(JSON.parse(raw));
      }
    } catch (error) {
      console.warn(`Failed to load ${key} from localStorage`, error);
    }
    return new Map<string, T>();
  }

  private persist<T>(key: string, map: Map<string, T>): void {
    try {
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(key, JSON.stringify(Array.from(map.entries())));
      }
    } catch (error) {
      console.warn(`Failed to persist ${key} to localStorage`, error);
    }
  }

  async findProgressByLine(lineEquipmentId: string): Promise<FieldInspectionProgress | null> {
    for (const item of this.progress.values()) {
      if (item.lineEquipmentId === lineEquipmentId) return item;
    }
    return null;
  }

  async listProgress(filter: FieldProgressFilter): Promise<FieldInspectionProgress[]> {
    const systemIds = filter.systemIds ? new Set(filter.systemIds) : null;
    return [...this.progress.values()].filter(item => {
      if (filter.clientId && item.clientId !== filter.clientId) return false;
      if (filter.projectId && item.projectId !== filter.projectId) return false;
      if (filter.unitAreaId && item.unitAreaId !== filter.unitAreaId) return false;
      if (systemIds && !systemIds.has(item.systemId)) return false;
      if (filter.lineEquipmentIds && !filter.lineEquipmentIds.includes(item.lineEquipmentId)) return false;
      return true;
    });
  }

  async saveProgress(progress: FieldInspectionProgress, expectedVersion: number): Promise<void> {
    const current = await this.findProgressByLine(progress.lineEquipmentId);
    const currentVersion = current ? current.version : 0;
    if (currentVersion !== expectedVersion) {
      // Mesma recusa do repositório SQLite: o serviço traduz para o aviso de
      // "atualizada por outro usuário" (item 41).
      throw new Error("FIELD_PROGRESS_VERSION_CONFLICT");
    }
    this.progress.set(progress.id, progress);
    this.persist(PROGRESS_KEY, this.progress);
  }

  async listAssignments(filter: FieldAssignmentFilter): Promise<FieldInspectionAssignment[]> {
    return [...this.assignments.values()].filter(item => {
      if (filter.clientId && item.clientId !== filter.clientId) return false;
      if (filter.inspectorProfessionalId && item.inspectorProfessionalId !== filter.inspectorProfessionalId) return false;
      if (filter.systemId && item.systemId !== filter.systemId) return false;
      if (filter.targetType && item.targetType !== filter.targetType) return false;
      if (filter.targetId && item.targetId !== filter.targetId) return false;
      if (filter.activeOnly && !item.active) return false;
      return true;
    });
  }

  async saveAssignment(assignment: FieldInspectionAssignment): Promise<void> {
    this.assignments.set(assignment.id, assignment);
    this.persist(ASSIGNMENT_KEY, this.assignments);
  }

  async findAssignmentById(id: string): Promise<FieldInspectionAssignment | null> {
    return this.assignments.get(id) ?? null;
  }
}
